// src/pages/Landing.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import Footer from "../components/Footer.jsx";
import { Link, useNavigate } from "react-router-dom";

/**
 * Hinweis:
 * - Bilder bitte nach /public/landing/ ablegen (siehe Liste weiter unten).
 * - Countdown-Datum unten in LAUNCH_AT anpassen.
 */

// ⏱ Zieltermin für den Serienstart/Anmeldeschluss – einfach anpassen
const LAUNCH_AT = "2025-12-22T19:50:00+01:00"; // Beispiel 19.12.2025 Berlin Timezone um 20:00
const YT_ID = "67yOodV_nxM";

function useCountdown(targetIso) {
    const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const diff = Math.max(0, target - now);
    const s = Math.floor(diff / 1000) % 60;
    const m = Math.floor(diff / 1000 / 60) % 60;
    const h = Math.floor(diff / 1000 / 60 / 60) % 24;
    const d = Math.floor(diff / 1000 / 60 / 60 / 24);
    return { d, h, m, s, zero: diff <= 0 };
}

export default function Landing() {
    const nav = useNavigate();
    const { d, h, m, s, zero } = useCountdown(LAUNCH_AT);
    const videoWrapRef = useRef(null);
    const [play, setPlay] = useState(false); // Autoplay (muted) per Default

    // Fallback: Autoplay kann auf manchen Geräten blockiert werden → Klick aktiviert
    useEffect(() => {
        const el = videoWrapRef.current;
        if (!el) return;
        const onInteract = () => setPlay(true);
        el.addEventListener("click", onInteract, { once: true });
        return () => el.removeEventListener("click", onInteract);
    }, []);

    return (
        <div className="landing">
            {/* HERO */}
            <header className="landing-hero">
                {/* 🔽 BACKGROUND-STACK: jetzt als ein PNG + Tint */}
                <div className="hero-bgstack" aria-hidden>
                    <img className="hero-bgimg" src="/landing/Hero-BG.svg" alt="" />
                    <div className="hero-tint" />
                </div>

                {/* NEU: zentrierte „Rail“ – wie cards-wrap */}
                <div className="landing-hero-inner">
                    <a className="landing-logo" href="/">
                        <img src="/landing/Racevision-Logo.svg" alt="RaceVision" />
                    </a>

                    {/* Top-Nav rechts */}
                    <nav className="landing-topnav">
                        <a href="https://discord.gg/2AteU7suuX" target="_blank" rel="noreferrer">Community</a>
                        <Link to="/gt3x/overview">Liga</Link>
                        <a href="https://www.youtube.com/@RacevisionTV" target="_blank" rel="noreferrer">Stream</a>
                        <button
                            className="btn-accent"
                            onClick={() => nav("/manager")}
                            aria-label="Zum Liga Manager"
                        >
                            zum Liga Manager
                        </button>
                    </nav>

                    {/* Claim + Text + Discord */}
                    <div className="hero-copy">
                        <h1 className="hero-title">
                            <span className="light">YOUR PLACE</span> <span className="outline">TO RACE.</span>
                        </h1>
                        <p className="hero-text">
                            RaceVision – eine Simracing-Community mit einer klaren Idee: Wir  teilen eine Vision von fairem,
                            leidenschaftlichem und professionell  organisiertem Racing für das gemeinsame Erlebnis auf der Strecke.
                            Wir entwickeln uns, unsere Fahrer und unsere Events ständig weiter, um das  beste Racing-Erlebnis für alle zu schaffen,
                            die Motorsport leben.
                        </p>
                        {/* NEU: Social-Row mit Instagram */}
                        <a className="btn-accent" href="https://discord.gg/2AteU7suuX" target="_blank" rel="noreferrer">
                            RaceVision Discord
                        </a>
                        <a
                            href="https://www.instagram.com/racevision.gg/"
                            target="_blank"
                            rel="noreferrer"
                            className="btn-ghost"
                        >
                            <img
                                src="/icons/instagram.svg"
                                alt="Instagram"
                                className="sidebar-social-icon"
                            />
                            <span>@racevision.gg</span>
                        </a>
                    </div>
                </div>

                {/* Sponsor-Keil JETZT im Hero */}
                <div className="landing-sponsor-bg" aria-hidden>
                    <picture>
                        <source
                            media="(max-width: 900px)"
                            srcSet="/landing/website-trenner-mobile.svg"
                        />
                        <img
                            className="sponsor-img"
                            src="/landing/website-trenner 3440.svg"
                            alt=""
                        />
                    </picture>
                </div>
            </header>

            {/* CARDS-BLOCK */}
            <section className="landing-cards">
                <div className="cards-wrap">
                    {/* Row 1: XL-Card allein, über gesamte Breite der Wrap, links/rechts stärker eingerückt */}
                    <article className="card-xl" onClick={() => nav("/gt3x/overview")}>
                        {/* Background-Stack (unterste → oberste Ebene) */}
                        <div className="card-xl__bg" aria-hidden>
                            <img className="card-xl__bgimg" src="/landing/CardXL-BG.svg" alt="" />
                        </div>

                        {/* 2-spaltiges Content-Grid */}
                        <div className="card-xl__grid">
                            {/* Linke Spalte: zentriertes Serienlogo */}
                            <div className="card-xl__left">
                                <img className="card-xl__logo" src="/landing/CardBG-SeriesLogo.png" alt="GT3Xperience" />
                            </div>

                            {/* Rechte Spalte: dein bestehender Content */}
                            <div className="card-xl__right">
                                <h3 className="card-xl__title">Season 1 goes live in</h3>
                                <div className="countdown">{zero ? "LIVE" : `${d}d ${h}h ${m}m ${s}s`}</div>
                                <div className="card-xl__actions">
                                    <a
                                        className="btn-accent"
                                        href="/gt3x/anmeldung/anmelden"
                                        onClick={(e) => e.stopPropagation()} /* verhindert, dass Artikel-Click greift */
                                    >
                                        Liga Anmeldung
                                    </a>
                                </div>
                            </div>
                        </div>
                    </article>

                    {/* Row 2: zwei Cards darunter */}
                    <div className="cards-row">
                        <a className="card-sm" href="https://discord.gg/2AteU7suuX" target="_blank" rel="noreferrer">
                            <img className="card-sm__img" src="/landing/league banner.png" alt="Discord" />
                        </a>

                        <a className="card-sm" href="https://www.bavariansimtec.com" target="_blank" rel="noreferrer">
                            <img className="card-sm__img" src="/landing/league banner bavarian.svg" alt="Bavarian Sim Tec" />
                        </a>
                    </div>
                </div>
            </section>

            {/* TEXTBLOCK "DIE LIGA" */}
            <section className="landing-info">
                <div className="info-wrap">
                    <p className="kicker">DIE LIGA</p>
                    <h2 className="kicker-sub">
                        GT3<span className="accent">X</span> SEASON 1
                    </h2>
                    <p className="lead">
                        <span className="lead-title">Season 1:</span> der Auftakt für RaceVision.<br />
                        In der GT3 treffen ambitionierte Fahrer, faire Duelle und echter Teamspirit aufeinander. <br/>
                        Sei von Anfang an dabei, wenn wir gemeinsam das Fundament für etwas Großes legen. <br/><span className="lead-title">Your Place to Race</span>
                    </p>
                    <p className="lead">
                        Melde dich über die Website an und werde Teil unserer Discord-Community <br/>dort passiert
                        alles, was RaceVision ausmacht.
                    </p>
                    <div className="cta-row">
                        <a className="btn-accent" href="/gt3x/anmeldung/anmelden">Liga-Anmeldung</a>
                        <Link className="btn-ghost" to="/gt3x/overview">GT3X Überblick</Link>
                    </div>
                </div>
            </section>

            {/* VIDEO + HINTERGRUNDVECTORS */}
            <section className="landing-video">
                <img className="rv-bg" src="/landing/Vector%20RV.svg" alt="" />
                <img className="union-right" src="/landing/Union rechts.svg" alt="" />

                <div className="video-wrap" ref={videoWrapRef} role="group" aria-label="RaceVision Video">
                    {!play ? (
                        <button
                            type="button"
                            className="video-thumbbtn"
                            onClick={() => setPlay(true)}
                            aria-label="Video abspielen"
                        >
                            {/* Playbutton als Bild */}
                            <img
                                className="video-play-img"
                                src="/landing/YouTube_play_button_icon.svg"
                                alt=""
                                aria-hidden="true"
                            />

                            {/* WENN ICH EIN ECHTES THUMNAIL NEHMEN WILL
                              <img
                                className="video-thumb"
                                src={`https://img.youtube.com/vi/${YT_ID}/maxresdefault.jpg`}
                                srcSet={`https://img.youtube.com/vi/${YT_ID}/maxresdefault.jpg 1280w,
                                https://img.youtube.com/vi/${YT_ID}/hqdefault.jpg 480w`}
                                sizes="(max-width: 800px) 100vw, 800px"
                                alt="Vorschaubild: RaceVision Interlagos"
                            />*/}
                            {/* AKTUELL FAKE EIGENES OBWOHL CAS STREAM */}
                            <img
                                className="video-thumb"
                                src="/landing/ADELAIDE - Youtube Thumbnail.png"
                                alt="RaceVision GT3X Season 1 Trailer"
                            />

                            <span className="video-play" aria-hidden="true">▶</span>
                        </button>
                    ) : (
                        <iframe
                            className="video-iframe"
                            title="RaceVision Rennen 3 - Adelaide"
                            src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1&mute=1&controls=1&rel=0&modestbranding=1&playsinline=1`}
                            allow="autoplay; encrypted-media; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                </div>
            </section>

            {/* TEXTBLOCK "DIE LIGA" */}
            <section className="landing-info">
                <div className="info-wrap">
                    <p className="kicker">DIE COMMUNITY</p>
                    <h2 className="kicker-sub">
                        WARUM RACEVISION<span className="accent">?</span>
                    </h2>
                    <p className="lead">
                        Weil wir glauben, dass Racing mehr ist als nur Zeitenfahren. Es ist <br/>
                        Competition, Emotion und Community. Gleichzeitig bieten wir Fahrern, die sich<br/>
                        weiterentwickeln  und ihre Performance steigern möchten, die ideale Umgebung.<br/>
                        Bei uns bekommst du
                        all das an einem Ort: <span className="lead-title">Your Place to Race</span>
                    </p>
                    <p className="lead">
                        <strong>Klare Strukturen & einfache Anmeldung</strong> <br />
                        Wir wollen Racing zugänglich machen. Wenige Schritte, <br/>
                        anmelden, fahren, Spaß haben. <br/>
                    </p>
                    <p className="lead">
                        <strong>Faires und transparentes Ligasystem</strong> <br />
                        Durchdachte Regeln, nachvollziehbare Wertungen und eine <br/>
                        Umgebung, in der sportliches Verhalten belohnt wird.
                    </p>
                    <p className="lead">
                        <strong>Community im Mittelpunkt</strong> <br />
                        RaceVision lebt von den Menschen, die mitfahren. Wir setzen auf <br/>
                        Austausch, Feedback und ein respektvolles Miteinander.
                    </p>

                    <div className="cta-row">
                        <a className="btn-ghost" href="https://discord.gg/2AteU7suuX" target="_blank" rel="noreferrer">
                            RaceVision Discord
                        </a>
                    </div>
                </div>
            </section>

            {/* GALERIE */}
            <section className="landing-gallery">
                <p className="kicker">GALERIE</p>
                <h2 className="kicker-sub">
                    GT3<span className="accent">X</span> IN ACTION
                </h2>

                <div className="gallery-grid">
                    <div className="gallery-item"><img src="/landing/GT3X-Bild1.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild2.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild3.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild4.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild6.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild5.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild7.png" alt="" /></div>
                    <div className="gallery-item"><img src="/landing/GT3X-Bild8.png" alt="" /></div>
                </div>

            </section>

            {/* FOOTER */}
            <div className="footer-vector">
                <img src="/landing/Racevision-Logo.svg" alt="" />
            </div>
            <Footer />
            <img className="footer-bar" src="/landing/RectangleYellow.svg" alt="" />
        </div>
    );
}
